import { Loader2, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'solid' | 'quiet' | 'outline' | 'ghost' | 'danger' | 'dangerSolid';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly block?: boolean;
  readonly icon?: LucideIcon;
  /**
   * Feedback is local to the control that caused it, never a page-level spinner
   * (CLAUDE.md §5a rule 4). The label stays in place and only fades, so the button
   * never resizes mid-action and shifts everything beside it.
   */
  readonly isPending?: boolean;
  /**
   * Held off by a limit (the job's Jev budget), not by missing input: the button stays off but keeps readable text on a muted surface,
   * instead of the faded copy of its own colour a plain disabled button gets (which is faint in the light theme).
   */
  readonly isBlocked?: boolean;
  readonly children?: ReactNode;
}

const ICON_SIZE: Readonly<Record<ButtonSize, number>> = { sm: 14, md: 16, lg: 18 };

/**
 * A button's look for an anchor. An action that goes somewhere is a real link, so Ctrl-click and
 * middle-click open it in a new tab (06-qol §8) — a button with a navigate handler cannot.
 */
export function buttonClassName(variant: ButtonVariant = 'quiet', size: ButtonSize = 'md'): string {
  return [styles.button, styles[variant], size !== 'md' ? styles[size] : ''].filter(Boolean).join(' ');
}

/** Forwards its ref so a button can be a Popover trigger without a wrapper. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'quiet',
    size = 'md',
    block = false,
    icon: Icon,
    isPending = false,
    isBlocked = false,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
): ReactNode {
  const isIconOnly = children === undefined;
  const classes = [
    buttonClassName(variant, size),
    block ? styles.block : '',
    isIconOnly ? styles.iconOnly : '',
    isPending ? styles.pending : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // data-variant lets a container restyle a button by its kind.
    <button {...rest} ref={ref} type={type} className={classes} data-variant={variant} data-blocked={isBlocked || undefined} disabled={disabled || isPending || isBlocked}>
      {isPending && (
        <span className={styles.pendingSpinner} aria-hidden>
          <Loader2 size={ICON_SIZE[size]} />
        </span>
      )}
      {Icon && <Icon size={ICON_SIZE[size]} aria-hidden />}
      {children}
    </button>
  );
});
